import { useMemo } from "react";

import {
    fetchIntegrationSpConfig,
    fetchIntegrationSpMetadataXml,
    setIntegrationProvider,
    testIntegrationSso
} from "../../../../api/admin/integrations/endpoints-referrers";
import type { Integration } from "../../../../common/interfaces/admin/integration.interface";
import SsoProviderConfig, {
    SsoProviderConfigApi
} from "../../../../components/admin/sso-provider-config/sso-provider-config.comp";

export interface IntegrationSsoConfigProps {
    companyId: number;
    integration: Integration;
    /** Called with the refreshed integration after a provider/test mutation. */
    onUpdated(updated: Integration): void;
}

/**
 * SSO tab of the embed integration editor: the shared SsoProviderConfig
 * control bound to the integration's provider endpoints (the tenant
 * Authentication tab binds the same control to the company-level ones).
 */
export default function IntegrationSsoConfig({
    companyId,
    integration,
    onUpdated
}: IntegrationSsoConfigProps) {
    const providerType = integration.providerType ?? "saml";

    // The test sign-in runs on the embed host (an authorized Firebase
    // domain), not this per-company admin subdomain. Derive that origin from
    // the integration's own embed URLs (all share the chat.<env> host).
    const chatOrigin = useMemo(() => {
        try {
            return new URL(integration.iframeUrl || integration.linkUrl || integration.scriptUrl)
                .origin;
        } catch {
            return null;
        }
    }, [integration.iframeUrl, integration.linkUrl, integration.scriptUrl]);

    const api = useMemo<SsoProviderConfigApi>(
        () => ({
            fetchSpConfig: () => fetchIntegrationSpConfig(companyId, integration.id),
            fetchSpMetadataXml: () => fetchIntegrationSpMetadataXml(companyId, integration.id),
            setProvider: body => setIntegrationProvider(companyId, integration.id, body),
            testSso: idToken => testIntegrationSso(companyId, integration.id, idToken)
        }),
        [companyId, integration.id]
    );

    return (
        <SsoProviderConfig
            providerType={providerType}
            state={integration}
            chatOrigin={chatOrigin}
            metadataFileName={`sp-metadata-${integration.id}.xml`}
            api={api}
            onConfigured={updated => onUpdated(updated as Integration)}
            onTested={result =>
                // Re-fetching is not needed: the result carries what the
                // activation gate reads.
                onUpdated({
                    ...integration,
                    lastSsoTestAt: result.testedAt,
                    lastSsoTestOutcome: result.outcome
                })
            }
            passedHint="This integration passed its test and can be activated (toggle at the top)."
            pendingHint="A passing test is required before this integration can be activated."
        />
    );
}
